import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Plus, 
  PackageCheck, 
  Building2, 
  Calendar, 
  Check,
  Clock,
  Filter,
  Search,
  X
} from 'lucide-react';
import { ErpPendingOrder, ErpPendingOrderItem } from './orders.types';
import { ProductsModel } from '../products/products.model';
import { ProductItem } from '../products/products.types';
import { ProductsController } from '../products/products.controller';
import api from '../../utils/api';

interface ErpValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderApproved: () => void;
}

export default function ErpValidationModal({ isOpen, onClose, onOrderApproved }: ErpValidationModalProps) {
  const [pendingOrders, setPendingOrders] = useState<ErpPendingOrder[]>([]);
  const [localProducts, setLocalProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'danger' | 'warning' | 'info'; msg: string; persistent?: boolean } | null>(null);
  const [rateLimitModal, setRateLimitModal] = useState<{ show: boolean; msg: string } | null>(null);

  // Date filters for ERP API (BR Format DD/MM/AAAA) - Default to empty
  const [dataIni, setDataIni] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  
  // Optional Order Number filter (non-mandatory, scoped to valid date range)
  const [orderNumberFilter, setOrderNumberFilter] = useState<string>('');

  // Helper to parse DD/MM/YYYY to valid Date
  const parseDateString = (str: string): Date | null => {
    if (!str || typeof str !== 'string') return null;
    const parts = str.trim().split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (day < 1 || day > 31 || month < 0 || month > 11 || year < 2000 || year > 2100) return null;
    const date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
    return date;
  };

  // Date range validation rules (Required, Start <= End, Interval <= 365 days / 1 year)
  const dateValidation = React.useMemo(() => {
    const trimmedIni = dataIni.trim();
    const trimmedFim = dataFim.trim();

    if (!trimmedIni && !trimmedFim) {
      return {
        isValid: false,
        days: null,
        status: 'EMPTY' as const,
        message: 'Preencha o período desejado (máximo de 1 ano / 365 dias) para filtrar pedidos do ERP.'
      };
    }

    if (trimmedIni.length < 10 || trimmedFim.length < 10) {
      return {
        isValid: false,
        days: null,
        status: 'INCOMPLETE' as const,
        message: 'Preencha ambas as datas completas no formato DD/MM/AAAA.'
      };
    }

    const dStart = parseDateString(trimmedIni);
    const dEnd = parseDateString(trimmedFim);

    if (!dStart || !dEnd) {
      return {
        isValid: false,
        days: null,
        status: 'INVALID_FORMAT' as const,
        message: 'Uma ou ambas as datas são inválidas. Verifique o dia, mês e ano.'
      };
    }

    const diffTime = dEnd.getTime() - dStart.getTime();
    const days = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (days < 0) {
      return {
        isValid: false,
        days,
        status: 'INVERTED' as const,
        message: 'A data inicial (DE) não pode ser posterior à data final (ATÉ).'
      };
    }

    if (days > 365) {
      return {
        isValid: false,
        days,
        status: 'EXCEEDED' as const,
        message: `O período selecionado (${days} dias) excede o limite máximo permitido de 1 ano (365 dias) pela API do ERP. Reduza o intervalo para habilitar a consulta.`
      };
    }

    return {
      isValid: true,
      days,
      status: 'VALID' as const,
      message: `Período válido: ${days === 0 ? 'mesmo dia' : `${days} dia${days > 1 ? 's' : ''}`}.`
    };
  }, [dataIni, dataFim]);

  // Quick period helpers
  const setQuickPeriod = (daysBack: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - daysBack);
    const pad = (n: number) => String(n).padStart(2, '0');
    setDataIni(`${pad(start.getDate())}/${pad(start.getMonth() + 1)}/${start.getFullYear()}`);
    setDataFim(`${pad(end.getDate())}/${pad(end.getMonth() + 1)}/${end.getFullYear()}`);
  };

  const clearDates = () => {
    setDataIni('');
    setDataFim('');
    setOrderNumberFilter('');
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for native date picker input
  const ddmmyyyyToIso = (val: string) => {
    if (!val || typeof val !== 'string') return '';
    const parts = val.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    return '';
  };

  // Convert YYYY-MM-DD from native date picker to DD/MM/YYYY
  const isoToDdmmyyyy = (val: string) => {
    if (!val || typeof val !== 'string') return '';
    const parts = val.split('-');
    if (parts.length === 3) {
      return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
    }
    return val;
  };

  // Auto-format Brazilian Date Input (DD/MM/AAAA)
  const handleDateMaskChange = (value: string, setter: (v: string) => void) => {
    // Remove non-numeric chars
    const cleaned = value.replace(/\D/g, '').slice(0, 8);
    let formatted = cleaned;
    if (cleaned.length > 2 && cleaned.length <= 4) {
      formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    } else if (cleaned.length > 4) {
      formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    }
    setter(formatted);
  };

  // States for mapping product directly
  const [selectedMapping, setSelectedMapping] = useState<{ [erpProductId: string]: string }>({});

  // Editable cities for each order
  const [orderCities, setOrderCities] = useState<{ [erpOrderId: string]: string }>({});

  // Quick product creation modal
  const [quickCreateItem, setQuickCreateItem] = useState<ErpPendingOrderItem | null>(null);
  const [quickName, setQuickName] = useState<string>('');
  const [quickCategory, setQuickCategory] = useState<string>('Coifas');

  // Classification helper for Accessories & Structural Parts
  const getItemClassification = (it: ErpPendingOrderItem, allOrderItems: ErpPendingOrderItem[], products: ProductItem[]) => {
    const mappedId = it.mappedProductId || (it as any).local_product_id;
    const erpProdId = it.idProdutoErp || (it as any).erp_product_id;

    const foundProd = products.find(p => String(p.id) === String(mappedId) || (erpProdId && String(p.erpProductId) === String(erpProdId)));
    if (!foundProd) return { type: 'UNKNOWN' as const, label: 'Pendente', category: '', parentName: null };

    const cat = (foundProd.category || '').toLowerCase();
    const name = (foundProd.name || '').toLowerCase();

    const isAccCat = cat.includes('acessór') || cat.includes('acessor') || cat.includes('accessori') || cat.includes('accessory');
    const isPartCat = cat.includes('part') || cat.includes('peça') || cat.includes('peca') || cat.includes('piece');

    // Checa se outro equipamento no pedido tem este produto na lista de accessories ou pieces
    let parentName: string | null = null;
    for (const other of allOrderItems) {
      const otherMappedId = other.mappedProductId || (other as any).local_product_id;
      if (!otherMappedId || String(otherMappedId) === String(mappedId)) continue;
      const otherProd = products.find(p => String(p.id) === String(otherMappedId));
      if (otherProd) {
        if (Array.isArray(otherProd.accessories) && otherProd.accessories.some(a => a.toLowerCase() === name || a.toLowerCase() === (foundProd.name || '').toLowerCase())) {
          parentName = otherProd.name;
          return { type: 'ACCESSORY' as const, label: 'Acessório Anexo', category: foundProd.category, parentName };
        }
        if (Array.isArray(otherProd.pieces) && otherProd.pieces.some(pc => pc.toLowerCase() === name || pc.toLowerCase() === (foundProd.name || '').toLowerCase())) {
          parentName = otherProd.name;
          return { type: 'PART' as const, label: 'Parte Estrutural', category: foundProd.category, parentName };
        }
      }
    }

    if (isAccCat) {
      // Localiza o equipamento principal (ex: Coifa) no mesmo pedido
      const mainEquip = allOrderItems.find(o => {
        const oMapId = o.mappedProductId || (o as any).local_product_id;
        if (!oMapId || String(oMapId) === String(mappedId)) return false;
        const op = products.find(p => String(p.id) === String(oMapId));
        return op && (op.category || '').toLowerCase().includes('coifa');
      });
      if (mainEquip) {
        const op = products.find(p => String(p.id) === String(mainEquip.mappedProductId || (mainEquip as any).local_product_id));
        if (op) parentName = op.name;
      }
      return { type: 'ACCESSORY' as const, label: 'Acessório Anexo', category: foundProd.category, parentName };
    }

    if (isPartCat) {
      const mainEquip = allOrderItems.find(o => {
        const oMapId = o.mappedProductId || (o as any).local_product_id;
        if (!oMapId || String(oMapId) === String(mappedId)) return false;
        const op = products.find(p => String(p.id) === String(oMapId));
        return op && (op.category || '').toLowerCase().includes('coifa');
      });
      if (mainEquip) {
        const op = products.find(p => String(p.id) === String(mainEquip.mappedProductId || (mainEquip as any).local_product_id));
        if (op) parentName = op.name;
      }
      return { type: 'PART' as const, label: 'Parte Estrutural', category: foundProd.category, parentName };
    }

    return { type: 'EQUIPMENT' as const, label: 'Equipamento Principal', category: foundProd.category, parentName: null };
  };

  useEffect(() => {
    if (isOpen) {
      loadPendingOrders();
      loadProducts();
    }
  }, [isOpen]);

  const triggerAlert = (type: 'success' | 'danger' | 'warning' | 'info', msg: string, persistent: boolean = false) => {
    setAlert({ type, msg, persistent });
    if (!persistent) {
      setTimeout(() => setAlert(null), 6000);
    }
  };

  const loadPendingOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/erp/pending');
      if (res.data) {
        const list: ErpPendingOrder[] = Array.isArray(res.data) ? res.data : (res.data.data || []);
        setPendingOrders(list);

        // Pre-fill order cities state
        setOrderCities(prev => {
          const updated = { ...prev };
          list.forEach((ord: any) => {
            const erpId = ord.idPedidoVenda || ord.erp_order_id || String(ord.id);
            if (!(erpId in updated)) {
              updated[erpId] = ord.nmCidade || ord.cidade || ord.city || '';
            }
          });
          return updated;
        });
      }
    } catch (err) {
      console.error("Erro ao carregar pedidos pendentes do ERP:", err);
      triggerAlert('danger', 'Não foi possível carregar os pedidos pendentes do ERP.');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const prods = await ProductsController.listFromServer('Todas');
      setLocalProducts(prods);
    } catch (err) {
      setLocalProducts(ProductsModel.getProducts());
    }
  };

  // Memoized filter for pending orders (if user enters order number, filter dynamically while preserving date range scope)
  const displayedOrders = React.useMemo(() => {
    const term = orderNumberFilter.trim().toLowerCase();
    if (!term) return pendingOrders;
    return pendingOrders.filter(ord => {
      const erpOrderId = String(ord.idPedidoVenda || (ord as any).erp_order_id || ord.id || '').toLowerCase();
      const customer = String(ord.nmCliente || (ord as any).customer_name || '').toLowerCase();
      return erpOrderId.includes(term) || customer.includes(term);
    });
  }, [pendingOrders, orderNumberFilter]);

  const handleSyncErp = async (force: boolean = false) => {
    if (!dateValidation.isValid) {
      triggerAlert('warning', dateValidation.message || 'Selecione um período de até 1 ano para consultar o ERP.');
      return;
    }

    const trimmedOrderNum = orderNumberFilter.trim();
    setSyncing(true);
    try {
      const res = await api.post('/erp/sync', {
        dataini: dataIni,
        datafim: dataFim,
        order_number: trimmedOrderNum,
        force: true
      });
      if (res.data && res.data.success) {
        if (res.data.notFoundForOrder) {
          triggerAlert(
            'warning', 
            res.data.message || `Nenhum pedido encontrado com o número "${trimmedOrderNum}" para o período selecionado (${dataIni} até ${dataFim}). Aguarde o tempo limite para novas consultas e tente novamente ajustando o período.`, 
            true
          );
        } else if (res.data.warning || res.data.isRateLimit) {
          triggerAlert('warning', res.data.message || res.data.warning || 'Aviso do ERP.', true);
        } else {
          triggerAlert('success', res.data.message || 'Sincronização com o ERP concluída!');
        }
        await loadPendingOrders();
        await loadProducts();
      } else {
        triggerAlert(
          'warning', 
          trimmedOrderNum
            ? `Nenhum pedido encontrado com o número "${trimmedOrderNum}" para o período selecionado (${dataIni} até ${dataFim}). Aguarde o tempo limite e tente novamente ajustando o período.`
            : 'A sincronização não retornou novos pedidos para o período selecionado.'
        );
        await loadPendingOrders();
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || '';
      const isRateLimit = err?.response?.status === 429 || 
        Boolean(err?.response?.data?.isRateLimit) ||
        errMsg.toLowerCase().includes('429') || 
        errMsg.toLowerCase().includes('limite') || 
        errMsg.toLowerCase().includes('aguarde') || 
        errMsg.toLowerCase().includes('repetir') ||
        errMsg.toLowerCase().includes('rate limit');

      if (isRateLimit) {
        triggerAlert('warning', errMsg || 'API ERP em modo de proteção temporária. Exibindo pedidos pendentes já armazenados no banco.', true);
      } else {
        triggerAlert('danger', errMsg || 'Falha ao conectar com o ERP ESW. Verifique as credenciais ou rede.');
      }
      await loadPendingOrders();
    } finally {
      setSyncing(false);
    }
  };

  const handleMapProduct = async (erpProductId: string, localProductId: string) => {
    if (!erpProductId || !localProductId) return;

    const localProd = localProducts.find(p => p.id === localProductId);
    setActionLoadingId(`map-${erpProductId}`);

    try {
      const res = await api.post('/erp/map-product', {
        idProdutoErp: erpProductId,
        erpProductId: erpProductId,
        erp_product_id: erpProductId,
        productId: localProductId,
        local_product_id: localProductId,
        local_product_name: localProd?.name || ''
      });

      if (res.data && res.data.success) {
        triggerAlert('success', `Produto ERP #${erpProductId} mapeado para "${localProd?.name}"!`);
        // Update local products
        if (localProd) {
          localProd.erpProductId = erpProductId;
          await ProductsController.handleSaveToServer(localProd.id, new FormData(), {
            accessories: localProd.accessories || [],
            pieces: localProd.pieces || [],
            productImageBase64: localProd.productImage || '',
            projectImage1Base64: localProd.projectImage1 || '',
            projectImage2Base64: localProd.projectImage2 || ''
          });
        }
        await loadPendingOrders();
        await loadProducts();
      } else {
        triggerAlert('danger', res.data?.message || res.data?.error || 'Erro ao mapear produto.');
      }
    } catch (err: any) {
      triggerAlert('danger', err?.response?.data?.message || err?.response?.data?.error || 'Erro na comunicação ao mapear produto.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleQuickCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickCreateItem) return;

    const erpProdId = quickCreateItem.idProdutoErp || (quickCreateItem as any).erp_product_id;
    const erpProdName = quickName || quickCreateItem.nmProdutoErp || (quickCreateItem as any).product_name || 'Produto sem nome';

    setActionLoadingId('quick-create');
    try {
      const formData = new FormData();
      formData.set('name', erpProdName);
      formData.set('category', quickCategory);
      formData.set('erpProductId', erpProdId);
      formData.set('model', `ERP #${erpProdId}`);
      formData.set('desc', `Produto cadastrado automaticamente a partir do ERP ESW (ID: ${erpProdId}).`);

      const res = await ProductsController.handleSaveToServer(null, formData, {
        accessories: [],
        pieces: [],
        productImageBase64: '',
        projectImage1Base64: '',
        projectImage2Base64: ''
      });

      if (res.success && res.product) {
        // Now map it
        await handleMapProduct(erpProdId, res.product.id);
        setQuickCreateItem(null);
        triggerAlert('success', `Novo produto "${res.product.name}" criado e vinculado ao ERP #${erpProdId}!`);
      } else {
        triggerAlert('danger', 'Erro ao salvar novo produto no catálogo.');
      }
    } catch (err) {
      triggerAlert('danger', 'Falha ao criar o produto.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleApproveOrder = async (idPedidoVenda: string) => {
    setActionLoadingId(`approve-${idPedidoVenda}`);
    const cityVal = orderCities[idPedidoVenda] ?? '';

    try {
      const res = await api.post('/erp/approve', { 
        idPedidoVenda: idPedidoVenda, 
        erp_order_id: idPedidoVenda,
        city: cityVal,
        cidade: cityVal
      });

      if (res.data && res.data.success) {
        triggerAlert('success', `Pedido ${idPedidoVenda} liberado para produção com sucesso!`);
        await loadPendingOrders();
        onOrderApproved();
      } else {
        triggerAlert('danger', res.data?.message || res.data?.error || 'Não foi possível aprovar o pedido.');
      }
    } catch (err: any) {
      triggerAlert('danger', err?.response?.data?.message || err?.response?.data?.error || 'Erro ao aprovar o pedido.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRejectOrder = async (idPedidoVenda: string) => {
    if (!window.confirm(`Tem certeza que deseja rejeitar o pedido do ERP ${idPedidoVenda}?`)) return;

    setActionLoadingId(`reject-${idPedidoVenda}`);
    try {
      const res = await api.post('/erp/reject', { 
        idPedidoVenda: idPedidoVenda, 
        erp_order_id: idPedidoVenda 
      });
      if (res.data && res.data.success) {
        triggerAlert('info', `Pedido ${idPedidoVenda} foi rejeitado.`);
        await loadPendingOrders();
      }
    } catch (err: any) {
      triggerAlert('danger', 'Erro ao rejeitar o pedido.');
    } finally {
      setActionLoadingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal fade show d-block" 
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1060 }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable my-2 my-sm-4 mx-2 mx-sm-auto" role="document">
        <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
          
          {/* Header Responsivo */}
          <div className="modal-header bg-dark text-white p-3 p-sm-4 d-flex flex-wrap align-items-center justify-content-between gap-2.5">
            <div className="d-flex align-items-center gap-2.5 min-w-0">
              <div className="p-2 p-sm-2.5 rounded-3 bg-primary bg-opacity-25 text-primary border border-primary border-opacity-25 flex-shrink-0">
                <PackageCheck size={20} className="d-none d-sm-block" />
                <PackageCheck size={18} className="d-block d-sm-none" />
              </div>
              <div className="min-w-0">
                <div className="text-muted text-xxs font-mono uppercase tracking-wider text-truncate">
                  Integração ESW ERP &gt; Fila de Validação
                </div>
                <h5 className="modal-title fw-black font-display mb-0 text-white text-sm-base text-truncate">
                  Painel de Validação e Liberação
                </h5>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
              <button 
                type="button" 
                className="btn-close btn-close-white p-2 ms-1" 
                onClick={onClose}
                aria-label="Close" 
              />
            </div>
          </div>

          {/* Body */}
          <div className="modal-body bg-light-subtle p-2.5 p-sm-4">

            {/* Alert banner */}
            {alert && (
              <div className={`alert alert-${alert.type === 'danger' ? 'danger' : alert.type} d-flex align-items-center justify-content-between mb-3 py-2 px-3 border-0 rounded-3 shadow-sm`}>
                <div className="d-flex align-items-center gap-2 min-w-0">
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <span className="font-sans text-xs sm:text-sm font-semibold text-truncate">{alert.msg}</span>
                </div>
                <button type="button" className="btn-close btn-close-sm flex-shrink-0 ms-2" onClick={() => setAlert(null)} />
              </div>
            )}

            {/* Status bar & Filtro Moderno de Período do ERP com botão único de filtragem */}
            <div className="card border-0 shadow-sm rounded-3 bg-white p-3 mb-3 mb-sm-4">
              <div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between gap-3">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="badge bg-warning bg-opacity-25 text-dark border border-warning font-mono text-xs px-2.5 py-1.5 rounded-pill fw-bold">
                    {orderNumberFilter.trim() 
                      ? `${displayedOrders.length} de ${pendingOrders.length} ${pendingOrders.length === 1 ? 'Pedido' : 'Pedidos'}` 
                      : `${pendingOrders.length} ${pendingOrders.length === 1 ? 'Pedido Pendente' : 'Pedidos Pendentes'}`}
                  </span>
                  <span className="text-muted text-xs font-sans">
                    Validação e liberação de pedidos importados do ERP.
                  </span>
                </div>

                {/* Filtro de Datas & Busca Opcional por Pedido com Validação de 1 Ano */}
                <div className="d-flex flex-column align-items-start align-items-xl-end gap-1.5 w-100 w-xl-auto">
                  <div className="d-flex flex-wrap align-items-center gap-2 w-100 w-xl-auto">
                    
                    {/* Input Nº Pedido (Opcional) */}
                    <div className="d-flex align-items-center bg-light border rounded-3 px-2.5 py-1.5 shadow-sm transition-all">
                      <Search size={14} className="text-primary me-1.5 opacity-75 flex-shrink-0" />
                      <span className="text-muted text-xxs font-mono uppercase fw-bold me-1">PEDIDO:</span>
                      <input 
                        type="text" 
                        className="form-control form-control-sm border-0 p-0 text-xs font-mono bg-transparent shadow-none"
                        style={{ width: '90px' }}
                        value={orderNumberFilter}
                        onChange={(e) => setOrderNumberFilter(e.target.value)}
                        placeholder="Nº (opcional)"
                        title="Informe o número do pedido para filtrar no período (opcional)"
                      />
                      {orderNumberFilter && (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-muted ms-1 text-decoration-none"
                          onClick={() => setOrderNumberFilter('')}
                          title="Limpar número do pedido"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>

                    {/* Input Data De (Obrigatório) */}
                    <div className={`d-flex align-items-center bg-light border rounded-3 px-2.5 py-1.5 shadow-sm transition-all ${
                      dateValidation.status === 'EXCEEDED' || dateValidation.status === 'INVERTED' ? 'border-danger bg-danger-subtle bg-opacity-10' : ''
                    }`}>
                      <Calendar size={14} className="text-primary me-1.5 opacity-75 flex-shrink-0" />
                      <span className="text-muted text-xxs font-mono uppercase fw-bold me-1">DE:</span>
                      <input 
                        type="text" 
                        className="form-control form-control-sm border-0 p-0 text-xs font-mono bg-transparent shadow-none"
                        style={{ width: '85px' }}
                        value={dataIni}
                        onChange={(e) => handleDateMaskChange(e.target.value, setDataIni)}
                        placeholder="DD/MM/AAAA"
                        title="Digite a data inicial no formato DD/MM/AAAA (obrigatório)"
                      />
                      <input 
                        type="date"
                        className="border-0 bg-transparent p-0 cursor-pointer text-muted"
                        style={{ width: '18px', opacity: 0.7 }}
                        value={ddmmyyyyToIso(dataIni)}
                        onChange={(e) => {
                          if (e.target.value) {
                            setDataIni(isoToDdmmyyyy(e.target.value));
                          }
                        }}
                        title="Clique para selecionar a data inicial no calendário"
                      />
                    </div>

                    {/* Input Data Até (Obrigatório) */}
                    <div className={`d-flex align-items-center bg-light border rounded-3 px-2.5 py-1.5 shadow-sm transition-all ${
                      dateValidation.status === 'EXCEEDED' || dateValidation.status === 'INVERTED' ? 'border-danger bg-danger-subtle bg-opacity-10' : ''
                    }`}>
                      <Calendar size={14} className="text-primary me-1.5 opacity-75 flex-shrink-0" />
                      <span className="text-muted text-xxs font-mono uppercase fw-bold me-1">ATÉ:</span>
                      <input 
                        type="text" 
                        className="form-control form-control-sm border-0 p-0 text-xs font-mono bg-transparent shadow-none"
                        style={{ width: '85px' }}
                        value={dataFim}
                        onChange={(e) => handleDateMaskChange(e.target.value, setDataFim)}
                        placeholder="DD/MM/AAAA"
                        title="Digite a data final no formato DD/MM/AAAA (obrigatório)"
                      />
                      <input 
                        type="date"
                        className="border-0 bg-transparent p-0 cursor-pointer text-muted"
                        style={{ width: '18px', opacity: 0.7 }}
                        value={ddmmyyyyToIso(dataFim)}
                        onChange={(e) => {
                          if (e.target.value) {
                            setDataFim(isoToDdmmyyyy(e.target.value));
                          }
                        }}
                        title="Clique para selecionar a data final no calendário"
                      />
                    </div>

                    {/* Botão Único de Filtragem & Sincronização */}
                    <button
                      type="button"
                      onClick={() => handleSyncErp(true)}
                      disabled={!dateValidation.isValid || syncing}
                      className={`btn btn-sm d-flex align-items-center gap-1.5 text-xs font-display py-1.5 px-3 fw-bold rounded-3 shadow-sm ${
                        dateValidation.isValid 
                          ? 'btn-primary' 
                          : 'btn-secondary bg-secondary text-white opacity-50'
                      }`}
                      style={{ cursor: dateValidation.isValid && !syncing ? 'pointer' : 'not-allowed' }}
                      title={
                        dateValidation.isValid 
                          ? (orderNumberFilter.trim() 
                              ? `Filtrar pedido #${orderNumberFilter.trim()} no período ${dataIni} até ${dataFim}`
                              : `Filtrar e sincronizar pedidos do ERP no período ${dataIni} até ${dataFim}`)
                          : (dateValidation.message || 'Preencha um período de até 1 ano para habilitar o botão')
                      }
                    >
                      <RefreshCw size={13} className={syncing ? 'spin' : ''} />
                      <span>{syncing ? 'Filtrando...' : 'Filtrar ERP'}</span>
                    </button>
                  </div>

                  {/* Atalhos rápidos de período */}
                  <div className="d-flex flex-wrap align-items-center gap-1 w-100 justify-content-start justify-content-xl-end">
                    <span className="text-xxs text-muted font-sans me-1">Atalhos:</span>
                    <button
                      type="button"
                      onClick={() => setQuickPeriod(30)}
                      className="btn btn-xs btn-outline-secondary py-0.5 px-1.5 font-mono text-xxs rounded"
                      title="Preencher últimos 30 dias"
                    >
                      30 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuickPeriod(90)}
                      className="btn btn-xs btn-outline-secondary py-0.5 px-1.5 font-mono text-xxs rounded"
                      title="Preencher últimos 90 dias"
                    >
                      90 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuickPeriod(180)}
                      className="btn btn-xs btn-outline-secondary py-0.5 px-1.5 font-mono text-xxs rounded"
                      title="Preencher últimos 6 meses"
                    >
                      180 dias
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuickPeriod(365)}
                      className="btn btn-xs btn-outline-secondary py-0.5 px-1.5 font-mono text-xxs rounded"
                      title="Preencher limite máximo de 1 ano"
                    >
                      1 ano (máx)
                    </button>
                    {(dataIni || dataFim || orderNumberFilter) && (
                      <button
                        type="button"
                        onClick={clearDates}
                        className="btn btn-xs btn-light text-danger py-0.5 px-1.5 font-sans text-xxs rounded ms-1"
                        title="Limpar campos de data e pedido"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Aviso dinâmico pré-clique sobre limite da API de 1 ano */}
              {dateValidation.status === 'EXCEEDED' && (
                <div className="alert alert-warning border border-warning bg-warning bg-opacity-10 d-flex align-items-center gap-2 p-2.5 mt-2.5 mb-0 rounded-3 text-xs font-sans text-dark shadow-sm animate-fadeIn">
                  <AlertTriangle size={16} className="text-warning flex-shrink-0" />
                  <div className="min-w-0">
                    <strong className="text-danger">Aviso de Limite da API:</strong> O período informado de <strong>{dateValidation.days} dias</strong> excede o limite máximo de <strong>1 ano (365 dias)</strong> permitido pela API do ERP. Reduza o intervalo para liberar o botão de filtragem e evitar bloqueios.
                  </div>
                </div>
              )}

              {dateValidation.status === 'INVERTED' && (
                <div className="alert alert-danger border border-danger bg-danger bg-opacity-10 d-flex align-items-center gap-2 p-2.5 mt-2.5 mb-0 rounded-3 text-xs font-sans text-danger shadow-sm animate-fadeIn">
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <div className="min-w-0">
                    <strong>Data Inválida:</strong> A data inicial <strong>DE ({dataIni})</strong> não pode ser posterior à data final <strong>ATÉ ({dataFim})</strong>.
                  </div>
                </div>
              )}

              {dateValidation.status === 'INVALID_FORMAT' && (
                <div className="alert alert-danger border border-danger bg-danger bg-opacity-10 d-flex align-items-center gap-2 p-2 mt-2.5 mb-0 rounded-2 text-xs font-sans text-danger shadow-sm animate-fadeIn">
                  <AlertTriangle size={15} className="flex-shrink-0" />
                  <span>{dateValidation.message}</span>
                </div>
              )}

              {dateValidation.status === 'VALID' && (
                <div className="d-flex align-items-center gap-1.5 text-success text-xxs font-mono mt-2 pt-1 border-top border-light">
                  <CheckCircle2 size={13} className="text-success" />
                  <span>Período válido para consulta: <strong>{dateValidation.days === 0 ? 'mesmo dia' : `${dateValidation.days} dia${dateValidation.days! > 1 ? 's' : ''}`}</strong> (dentro do limite permitido pela API do ERP).</span>
                </div>
              )}

              {(dateValidation.status === 'EMPTY' || dateValidation.status === 'INCOMPLETE') && (
                <div className="d-flex align-items-center gap-1.5 text-muted text-xxs font-sans mt-2 pt-1 border-top border-light">
                  <Clock size={12} className="opacity-75" />
                  <span>A API do ERP aceita intervalos de até <strong>365 dias (1 ano)</strong>. Preencha as datas ou escolha um atalho acima para habilitar o botão <em>Filtrar ERP</em>.</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status"></div>
                <p className="text-muted font-mono text-sm">Carregando pedidos importados do ERP...</p>
              </div>
            ) : pendingOrders.length === 0 ? (
              <div className="card border-0 shadow-sm p-4 p-sm-5 text-center bg-white rounded-3">
                <CheckCircle2 size={44} className="text-success mx-auto mb-3 opacity-75" />
                <h5 className="fw-bold font-display text-dark mb-1">Nenhum Pedido Pendente no Momento!</h5>
                <p className="text-muted text-xs sm:text-sm font-sans max-w-md mx-auto mb-3">
                  Todos os pedidos vindos do ERP já foram mapeados e liberados para a linha de produção, ou o período selecionado não retornou novos pedidos.
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <button 
                    onClick={() => handleSyncErp(false)} 
                    disabled={syncing}
                    className="btn btn-outline-primary btn-sm font-display d-flex align-items-center gap-2 px-3 py-1.5"
                  >
                    <RefreshCw size={14} className={syncing ? 'spin' : ''} /> Buscar Pedidos da API ERP
                  </button>
                  <button 
                    onClick={() => handleSyncErp(true)} 
                    disabled={syncing}
                    className="btn btn-primary btn-sm font-display d-flex align-items-center gap-2 px-3 py-1.5"
                  >
                    <RefreshCw size={14} className={syncing ? 'spin' : ''} /> Recarregar Lote de Pedidos ERP
                  </button>
                </div>
              </div>
            ) : displayedOrders.length === 0 ? (
              <div className="card border-0 shadow-sm p-4 p-sm-5 text-center bg-white rounded-3">
                <Search size={44} className="text-muted mx-auto mb-3 opacity-50" />
                <h5 className="fw-bold font-display text-dark mb-1">Nenhum Pedido Encontrado!</h5>
                <p className="text-muted text-xs sm:text-sm font-sans max-w-md mx-auto mb-3">
                  Nenhum pedido pendente corresponde ao número <strong>"{orderNumberFilter}"</strong> entre os {pendingOrders.length} pedidos pendentes carregados.
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <button 
                    onClick={() => setOrderNumberFilter('')} 
                    className="btn btn-outline-secondary btn-sm font-display px-3 py-1.5"
                  >
                    Limpar Busca por Pedido
                  </button>
                  <button 
                    onClick={() => handleSyncErp(true)} 
                    disabled={syncing || !dateValidation.isValid}
                    className="btn btn-primary btn-sm font-display d-flex align-items-center gap-2 px-3 py-1.5"
                  >
                    <RefreshCw size={14} className={syncing ? 'spin' : ''} /> Consultar no ERP com Período
                  </button>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3 gap-sm-4">
                {displayedOrders.map((ord) => {
                  const erpOrderId = ord.idPedidoVenda || (ord as any).erp_order_id || String(ord.id);
                  const customerName = ord.nmCliente || (ord as any).customer_name || 'Cliente ERP';
                  const departureDate = ord.dtPedidoVenda || (ord as any).departure || 'A combinar';
                  const sellerName = ord.nmVendedor ? ` | Vendedor: ${ord.nmVendedor}` : '';

                  const hasUnmapped = ord.items.some(it => !it.isMapped && !(it as any).local_product_id);

                  return (
                    <div key={ord.id} className="card shadow-sm border rounded-3 overflow-hidden bg-white">
                      
                      {/* Order Header Responsivo */}
                      <div className="card-header bg-light p-3 d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 border-bottom">
                        
                        {/* Informações do Pedido + Campo Cidade */}
                        <div className="d-flex flex-wrap align-items-center gap-2.5 w-100 w-lg-auto">
                          <span className="badge bg-primary font-mono text-xs px-2.5 py-1.5 rounded">
                            Pedido ERP #{erpOrderId}
                          </span>

                          <div className="min-w-0">
                            <h6 className="fw-bold text-dark font-display mb-0 text-truncate">{customerName}</h6>
                            <span className="text-muted text-xs font-mono d-flex align-items-center gap-1 mt-0.5">
                              <Calendar size={12} /> Saída: {departureDate}{sellerName}
                            </span>
                          </div>

                          {/* Campo Cidade Interativo e Editável */}
                          <div 
                            className="d-flex align-items-center gap-1.5 bg-white border rounded px-2.5 py-1 shadow-2xs ms-0 ms-sm-2 w-100 w-sm-auto" 
                            style={{ minWidth: '170px', maxWidth: '240px' }}
                          >
                            <Building2 size={13} className="text-primary flex-shrink-0" />
                            <input
                              type="text"
                              className="form-control form-control-sm border-0 p-0 text-xs shadow-none font-sans"
                              placeholder="Cidade (opcional)"
                              title="Informe a cidade do pedido antes de liberar"
                              value={orderCities[erpOrderId] ?? ''}
                              onChange={(e) => setOrderCities(prev => ({ ...prev, [erpOrderId]: e.target.value }))}
                            />
                          </div>
                        </div>

                        {/* Status de Mapeamento + Ações de Liberação */}
                        <div className="d-flex flex-wrap align-items-center justify-content-between justify-content-lg-end gap-2 w-100 w-lg-auto pt-2 pt-lg-0 border-top border-lg-0">
                          {hasUnmapped ? (
                            <span className="badge bg-warning bg-opacity-25 text-dark border border-warning font-mono text-xs px-2.5 py-1.5">
                              <AlertTriangle size={12} className="me-1" /> Requer Mapeamento
                            </span>
                          ) : (
                            <span className="badge bg-success bg-opacity-25 text-success border border-success font-mono text-xs px-2.5 py-1.5">
                              <CheckCircle2 size={12} className="me-1" /> 100% Mapeado
                            </span>
                          )}

                          <div className="d-flex align-items-center gap-2 ms-auto ms-lg-0">
                            <button
                              type="button"
                              onClick={() => handleApproveOrder(erpOrderId)}
                              disabled={hasUnmapped || actionLoadingId === `approve-${erpOrderId}`}
                              className="btn btn-success d-flex align-items-center gap-1.5 text-xs font-display fw-bold px-3 py-1.5 shadow-sm"
                              style={{ borderRadius: '6px' }}
                              title={hasUnmapped ? 'Mapeie todos os produtos abaixo antes de liberar para produção' : 'Liberar pedido para a lista de produção'}
                            >
                              <PackageCheck size={14} /> Liberar para Produção
                            </button>

                            <button
                              type="button"
                              onClick={() => handleRejectOrder(erpOrderId)}
                              disabled={actionLoadingId === `reject-${erpOrderId}`}
                              className="btn btn-outline-danger d-flex align-items-center gap-1 text-xs font-display px-2.5 py-1.5"
                              style={{ borderRadius: '6px' }}
                              title="Rejeitar e descartar este pedido do ERP"
                            >
                              <XCircle size={14} />
                            </button>
                          </div>
                        </div>

                      </div>

                      {/* Items List - Tabela no Desktop / Cards no Mobile (PWA / APK) */}
                      <div className="card-body p-0">
                        
                        {/* Visualização Desktop (Tabela Completa) */}
                        <div className="d-none d-md-block table-responsive">
                          <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.88rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th className="px-4 py-2.5 text-xs font-mono uppercase text-muted" style={{ width: '110px' }}>ID ERP</th>
                                <th className="py-2.5 text-xs font-mono uppercase text-muted">Produto no ERP</th>
                                <th className="py-2.5 text-xs font-mono uppercase text-muted text-center" style={{ width: '70px' }}>Qtd</th>
                                <th className="py-2.5 text-xs font-mono uppercase text-muted">Produto Mapeado no Controle</th>
                                <th className="px-4 py-2.5 text-xs font-mono uppercase text-muted text-end" style={{ width: '230px' }}>Ação de Mapeamento</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ord.items.map((it, idx) => {
                                const erpProdId = it.idProdutoErp || (it as any).erp_product_id || '';
                                const erpProdName = it.nmProdutoErp || (it as any).product_name || 'Produto sem nome no ERP';
                                const qty = it.quantidade || (it as any).qty || 1;
                                const mappedId = it.mappedProductId || (it as any).local_product_id || null;
                                const mappedName = it.mappedProductName || (it as any).local_product_name || null;
                                const isMapped = it.isMapped || !!mappedId;
                                const selectedLocalId = selectedMapping[erpProdId] || '';
                                const classification = getItemClassification(it, ord.items, localProducts);

                                return (
                                  <tr key={idx} className="border-bottom">
                                    <td className="px-4 py-3 font-mono fw-bold text-primary">
                                      #{erpProdId}
                                    </td>
                                    <td className="py-3">
                                      <span className="fw-semibold text-dark font-sans d-block">{erpProdName}</span>
                                    </td>
                                    <td className="py-3 text-center font-mono font-bold">
                                      {qty}
                                    </td>
                                    <td className="py-3">
                                      {isMapped ? (
                                        <div className="d-flex flex-column gap-1">
                                          <div className="d-flex align-items-center gap-2 text-success fw-semibold font-sans">
                                            <CheckCircle2 size={16} />
                                            <span>{mappedName || 'Produto Mapeado'}</span>
                                            <span className="badge bg-success bg-opacity-10 text-success font-mono text-xxs">ID #{mappedId}</span>
                                          </div>
                                          
                                          {/* Badge de Classificação: Acessório, Parte ou Equipamento */}
                                          {classification.type === 'ACCESSORY' && (
                                            <div className="d-flex align-items-center gap-1.5 mt-0.5">
                                              <span 
                                                className="badge bg-info bg-opacity-15 text-info-emphasis border border-info border-opacity-40 font-mono text-xxs px-2 py-0.5 rounded"
                                                title="Este item é um acessório e será vinculado ao equipamento principal (não cria item avulso na linha de produção)"
                                              >
                                                ↳ Acessório {classification.parentName ? `de: ${classification.parentName}` : 'Anexo'}
                                              </span>
                                              <span className="text-muted text-xxs font-sans">
                                                (Entra no checklist da Coifa/Equipamento)
                                              </span>
                                            </div>
                                          )}

                                          {classification.type === 'PART' && (
                                            <div className="d-flex align-items-center gap-1.5 mt-0.5">
                                              <span 
                                                className="badge bg-secondary bg-opacity-15 text-secondary border border-secondary border-opacity-40 font-mono text-xxs px-2 py-0.5 rounded"
                                                title="Este item é uma parte estrutural e será agrupado no equipamento principal"
                                              >
                                                ↳ Parte {classification.parentName ? `de: ${classification.parentName}` : 'Estrutural'}
                                              </span>
                                              <span className="text-muted text-xxs font-sans">
                                                (Composição da Coifa/Equipamento)
                                              </span>
                                            </div>
                                          )}

                                          {classification.type === 'EQUIPMENT' && (
                                            <div className="d-flex align-items-center gap-1.5 mt-0.5">
                                              <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 font-mono text-xxs px-2 py-0.5 rounded">
                                                {classification.category || 'Equipamento Principal'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="d-flex align-items-center gap-2 text-warning fw-semibold font-sans">
                                          <AlertTriangle size={16} />
                                          <span className="text-dark">Não encontrado no Controle</span>
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-end">
                                      <div className="d-flex align-items-center justify-content-end gap-2">
                                        <select
                                          className="form-select form-select-sm font-sans"
                                          style={{ maxWidth: '210px', cursor: 'pointer', fontSize: '0.8rem' }}
                                          value={isMapped && mappedId ? String(mappedId) : selectedLocalId}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setSelectedMapping(prev => ({ ...prev, [erpProdId]: val }));
                                            if (val) {
                                              handleMapProduct(erpProdId, val);
                                            }
                                          }}
                                        >
                                          <option value="">-- Selecionar Produto --</option>
                                          {localProducts.map(p => (
                                            <option key={p.id} value={p.id}>
                                              {p.name} ({p.category})
                                            </option>
                                          ))}
                                        </select>

                                        {!isMapped && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setQuickCreateItem(it);
                                              setQuickName(erpProdName);
                                            }}
                                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 text-xs font-display whitespace-nowrap"
                                            style={{ borderRadius: '6px' }}
                                            title="Cadastrar este produto rapidamente no controle"
                                          >
                                            <Plus size={12} /> Cadastrar
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Visualização Mobile Otimizada para PWA / APK (Cards Empilhados) */}
                        <div className="d-block d-md-none p-2.5 bg-light bg-opacity-25">
                          <div className="d-flex flex-column gap-2.5">
                            {ord.items.map((it, idx) => {
                              const erpProdId = it.idProdutoErp || (it as any).erp_product_id || '';
                              const erpProdName = it.nmProdutoErp || (it as any).product_name || 'Produto sem nome no ERP';
                              const qty = it.quantidade || (it as any).qty || 1;
                              const mappedId = it.mappedProductId || (it as any).local_product_id || null;
                              const mappedName = it.mappedProductName || (it as any).local_product_name || null;
                              const isMapped = it.isMapped || !!mappedId;
                              const selectedLocalId = selectedMapping[erpProdId] || '';
                              const classification = getItemClassification(it, ord.items, localProducts);

                              return (
                                <div key={idx} className="card border rounded-3 p-3 bg-white shadow-2xs">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="badge bg-primary bg-opacity-10 text-primary font-mono text-xs fw-bold">
                                      Item ERP #{erpProdId}
                                    </span>
                                    <span className="badge bg-dark text-white font-mono text-xs">
                                      Qtd: {qty}
                                    </span>
                                  </div>

                                  <h6 className="fw-bold text-dark font-sans text-xs sm:text-sm mb-2">{erpProdName}</h6>

                                  <div className="mb-2">
                                    {isMapped ? (
                                      <div className="d-flex flex-column gap-1 bg-success bg-opacity-10 p-2 rounded">
                                        <div className="d-flex align-items-center gap-1.5 text-success fw-semibold font-sans text-xs">
                                          <CheckCircle2 size={14} className="flex-shrink-0" />
                                          <span className="text-truncate">{mappedName || 'Mapeado'}</span>
                                        </div>

                                        {classification.type === 'ACCESSORY' && (
                                          <span className="badge bg-info bg-opacity-25 text-info-emphasis border border-info font-mono text-xxs align-self-start mt-0.5">
                                            ↳ Acessório {classification.parentName ? `de ${classification.parentName}` : 'Anexo'}
                                          </span>
                                        )}

                                        {classification.type === 'PART' && (
                                          <span className="badge bg-secondary bg-opacity-25 text-secondary border border-secondary font-mono text-xxs align-self-start mt-0.5">
                                            ↳ Parte {classification.parentName ? `de ${classification.parentName}` : 'Estrutural'}
                                          </span>
                                        )}

                                        {classification.type === 'EQUIPMENT' && (
                                          <span className="badge bg-primary bg-opacity-15 text-primary font-mono text-xxs align-self-start mt-0.5">
                                            {classification.category || 'Equipamento Principal'}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="d-flex align-items-center gap-1.5 text-warning fw-semibold font-sans text-xs bg-warning bg-opacity-10 p-2 rounded">
                                        <AlertTriangle size={14} className="flex-shrink-0" />
                                        <span>Não encontrado no Controle</span>
                                      </div>
                                    )}
                                  </div>

                                  <div className="d-flex flex-column gap-2 mt-1">
                                    <select
                                      className="form-select form-select-sm font-sans w-100 text-xs"
                                      style={{ cursor: 'pointer' }}
                                      value={isMapped && mappedId ? String(mappedId) : selectedLocalId}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedMapping(prev => ({ ...prev, [erpProdId]: val }));
                                        if (val) {
                                          handleMapProduct(erpProdId, val);
                                        }
                                      }}
                                    >
                                      <option value="">-- Selecionar Produto --</option>
                                      {localProducts.map(p => (
                                        <option key={p.id} value={p.id}>
                                          {p.name} ({p.category})
                                        </option>
                                      ))}
                                    </select>

                                    {!isMapped && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setQuickCreateItem(it);
                                          setQuickName(erpProdName);
                                        }}
                                        className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1 text-xs font-display py-1.5"
                                        style={{ borderRadius: '6px' }}
                                      >
                                        <Plus size={12} /> Cadastrar Produto
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quick Create Submodal */}
            {quickCreateItem && (
              <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1070 }}>
                <div className="modal-dialog modal-dialog-centered my-3 mx-2 mx-sm-auto">
                  <div className="modal-content border-0 rounded-4 shadow-lg">
                    <div className="modal-header bg-primary text-white py-3 px-4">
                      <h6 className="modal-title font-display fw-bold mb-0">Cadastrar Novo Produto vindo do ERP</h6>
                      <button type="button" className="btn-close btn-close-white" onClick={() => setQuickCreateItem(null)} />
                    </div>
                    <form onSubmit={handleQuickCreateProduct} className="modal-body p-4">
                      <div className="mb-3">
                        <label className="form-label text-xxs font-mono uppercase text-muted fw-bold">Código no ERP</label>
                        <input type="text" className="form-control bg-light font-mono text-primary fw-bold" value={`#${quickCreateItem.idProdutoErp || (quickCreateItem as any).erp_product_id}`} disabled />
                      </div>

                      <div className="mb-3">
                        <label className="form-label text-xxs font-mono uppercase text-muted fw-bold">Nome do Produto</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={quickName} 
                          onChange={(e) => setQuickName(e.target.value)} 
                          required 
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label text-xxs font-mono uppercase text-muted fw-bold">Categoria</label>
                        <select 
                          className="form-select" 
                          value={quickCategory} 
                          onChange={(e) => setQuickCategory(e.target.value)}
                        >
                          <option value="Coifas">Coifas</option>
                          <option value="Dutos">Dutos</option>
                          <option value="Filtros">Filtros</option>
                          <option value="Curvas">Curvas</option>
                          <option value="Acessórios">Acessórios</option>
                          <option value="Chaminés">Chaminés</option>
                          <option value="Tubulações">Tubulações</option>
                        </select>
                      </div>

                      <div className="d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-light btn-sm" onClick={() => setQuickCreateItem(null)}>Cancelar</button>
                        <button 
                          type="submit" 
                          disabled={actionLoadingId === 'quick-create'}
                          className="btn btn-primary btn-sm font-display fw-bold d-flex align-items-center gap-1"
                        >
                          <Check size={14} /> Salvar e Mapear
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de Aviso de Limite de Tempo Excedido (Rate Limit) - Fechamento Manual */}
            {rateLimitModal && rateLimitModal.show && (
              <div 
                className="modal fade show d-block" 
                style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1080 }} 
                tabIndex={-1} 
                role="dialog"
              >
                <div className="modal-dialog modal-dialog-centered my-3 mx-2 mx-sm-auto" style={{ maxWidth: '480px' }}>
                  <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
                    <div className="modal-header bg-warning bg-opacity-15 border-0 pt-4 px-4 pb-2">
                      <div className="d-flex align-items-center gap-2.5">
                        <div className="p-2.5 rounded-3 bg-warning text-dark flex-shrink-0 shadow-sm">
                          <Clock size={22} className="fw-bold" />
                        </div>
                        <div>
                          <h6 className="modal-title font-display fw-bold text-dark mb-0">
                            Limite de Tempo Excedido
                          </h6>
                          <span className="text-muted text-xxs font-mono uppercase">
                            Proteção de Consultas ERP ESW
                          </span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setRateLimitModal(null)} 
                        aria-label="Close" 
                      />
                    </div>

                    <div className="modal-body px-4 py-3">
                      <div className="p-3 bg-light rounded-3 border mb-3">
                        <p className="text-dark font-sans text-sm mb-0 lh-base fw-medium">
                          O servidor da <strong>ESW</strong> possui uma regra de segurança que limita requisições sucessivas num curto intervalo.
                        </p>
                      </div>

                      <div className="alert alert-warning border-0 rounded-3 py-2.5 px-3 d-flex align-items-center gap-2 mb-0">
                        <AlertTriangle size={18} className="text-warning flex-shrink-0" />
                        <span className="text-xs font-sans fw-bold text-dark">
                          Por favor, aguarde cerca de 5 minutos antes de realizar uma nova filtragem na API.
                        </span>
                      </div>

                      {rateLimitModal.msg && (
                        <div className="mt-3 pt-2 border-top">
                          <span className="text-muted text-xxs font-mono d-block">Detalhes retornados pelo ERP:</span>
                          <code className="text-danger text-xxs font-mono bg-danger bg-opacity-10 p-1.5 rounded d-block text-break mt-1">
                            {rateLimitModal.msg}
                          </code>
                        </div>
                      )}
                    </div>

                    <div className="modal-footer bg-light border-0 px-4 py-3 d-flex justify-content-end">
                      <button 
                        type="button" 
                        className="btn btn-dark btn-sm font-display fw-bold px-4 py-2 rounded-3 shadow-sm d-flex align-items-center gap-1.5"
                        onClick={() => setRateLimitModal(null)}
                      >
                        <Check size={14} />
                        <span>Entendido (Fechar)</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Footer Responsivo */}
          <div className="modal-footer bg-light py-3 px-3 px-sm-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
            <span className="text-muted text-xs font-sans text-center text-sm-start">
              Os pedidos aprovados neste painel entram automaticamente para a fila principal na etapa Corte.
            </span>
            <button type="button" className="btn btn-secondary font-display text-sm py-1.5 py-sm-2 px-4 w-100 w-sm-auto" onClick={onClose}>
              Fechar
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
